import { useState, useEffect } from 'react';
import { db } from '../../../config/firebase';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';

export function useProductShowcaseData() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrowsingGroup, setSelectedBrowsingGroup] = useState('all');
  const [availableBrowsingGroups, setAvailableBrowsingGroups] = useState([]);

  // Load products from Firestore filtered by BrowsingGroup
  const loadProducts = async () => {
    setLoading(true);
    try {
      const productsCollection = collection(db, 'products');
      let q;
      
      // Query based on selected BrowsingGroup
      if (selectedBrowsingGroup === 'all') {
        // First, get all unique BrowsingGroup values from the database
        const allProductsQuery = query(productsCollection, where('isActive', '!=', false));
        const allProductsSnapshot = await getDocs(allProductsQuery);
        
        // Extract unique BrowsingGroup values
        const uniqueGroups = new Set();
        allProductsSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.BrowsingGroup && data.isActive !== false) {
            uniqueGroups.add(data.BrowsingGroup);
          }
        });
        
        const groupsArray = Array.from(uniqueGroups).sort();
        setAvailableBrowsingGroups(groupsArray);
        
        // Now query for all products with any BrowsingGroup value
        if (groupsArray.length > 0) {
          q = query(productsCollection, where('BrowsingGroup', 'in', groupsArray));
        } else {
          // No BrowsingGroup products found
          setProducts([]);
          setCategories([]);
          setLoading(false);
          return;
        }
      } else {
        // Get products with specific BrowsingGroup value
        q = query(productsCollection, where('BrowsingGroup', '==', selectedBrowsingGroup));
      }
      
      const productSnapshot = await getDocs(q);
      const productsData = [];
      
      productSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.isActive !== false) { // Only include active products
          productsData.push({
            id: doc.id,
            code: data.code || doc.id,
            name: data.name || '',
            description: data.description || '',
            category: data.category || '',
            Categories: data.Categories || '',
            USP: data.USP || '',
            imageUrl: data.imageUrl || '',
            prices: data.prices || { USD: 0 },
            stock: data.stock || 0,
            isStandaloneProduct: data.isStandaloneProduct !== false,
            BrowsingGroup: data.BrowsingGroup || null,
            giftLevel: data.giftLevel || null,
            PntPlsssCode: data.PntPlsssCode || ''
          });
        }
      });
      
      console.log(`Loaded ${productsData.length} products for BrowsingGroup: ${selectedBrowsingGroup}`);
      setProducts(productsData);
      
      // Extract categories from Categories field (multi-tag support)
      const categorySet = new Set();
      productsData.forEach(product => {
        if (product.Categories) {
          // Split multiple categories and extract clean category names
          const categoryList = product.Categories.split(',').map(cat => {
            const cleanCat = cat.trim();
            // Extract the description part after the code
            const match = cleanCat.match(/^[^ ]+ (.+)$/);
            return match ? match[1].trim() : cleanCat;
          });
          categoryList.forEach(cat => {
            if (cat && cat !== '') categorySet.add(cat);
          });
        }
      });
      setCategories(Array.from(categorySet).sort());
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Get gift data for a specific product
  const getGiftData = async (productId) => {
    try {
      const docRef = doc(db, 'products_gifts', productId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return data.giftSets || {};
      }
      return {};
    } catch (error) {
      console.error(`Error fetching gift data for ${productId}:`, error);
      return {};
    }
  };

  // Get gift item details
  const getGiftItemDetails = async (itemId) => {
    try {
      const docRef = doc(db, 'products', itemId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: itemId,
          name: data.name || `Gift Item ${itemId}`,
          description: data.description || '',
          USP: data.USP || '',
          imageUrl: data.imageUrl || '',
          category: data.category || 'Gift'
        };
      }
      return null;
    } catch (error) {
      console.error(`Error fetching gift item details for ${itemId}:`, error);
      return null;
    }
  };

  useEffect(() => {
    loadProducts();
  }, [selectedBrowsingGroup]);

  return {
    products,
    categories,
    loading,
    selectedBrowsingGroup,
    setSelectedBrowsingGroup,
    availableBrowsingGroups,
    getGiftData,
    getGiftItemDetails,
    loadProducts
  };
}
